import {
  QuestionStatus,
  type QuizQuestion,
  type QuizWinnerUser,
} from "@/generated/graphql";
import { toValue } from "vue";
import {
  computed,
  inject,
  provide,
  reactive,
  ref,
  watch,
  type InjectionKey,
  type MaybeRef,
} from "vue";
import { ContestType, type IContestFinished } from "../contest.types";
import { isString } from "@/utils/types";

const quizCardProvideKey = Symbol("quizCardProvideKey") as InjectionKey<
  ReturnType<typeof provideQuizCardState>
>;

// Generic type to accept any Quiz-like object with the required fields
export type QuizLike = {
  id: string;
  status?: string | null;
  quiz_questions?: QuizQuestion[] | null;
  winner_users?: QuizWinnerUser[] | null;
  prize?: {
    id: string;
    title?: string | null;
    description?: string | null;
    image?: string | null;
    contest_prize_translations: Array<{
      title?: string | null;
      description?: string | null;
    }>;
  } | null;
  created_by_id?: string | null;
  created_by?: {
    id: string;
    username?: string | null;
  } | null;
  description?: string | null;
  end_time?: string | null;
  quiz_translations?: Array<{ description?: string | null }> | null;
  quiz_communities?: Array<{
    community?: {
      id: string;
      name?: string | null;
    } | null;
  }> | null;
  authorization_rule_group?: unknown;
};

export function provideQuizCardState(quizResult: MaybeRef<QuizLike | null>) {
  const questions = reactive<QuizQuestion[]>([]);
  const winners = ref<QuizWinnerUser[] | null>(null);

  const lastVotedQuestionId = ref<string | null>(null);
  const currentQuestionIdx = ref<number | null>(null);
  const currentQuestion = computed(() =>
    currentQuestionIdx.value !== null
      ? (questions[currentQuestionIdx.value] ?? null)
      : null,
  );

  const selectedOptionId = ref<string | null>(null);

  const quizStatus = computed(() => quiz.value?.status);
  watch(
    () => toValue(quizResult),
    (value) => {
      if (!value) return;

      if (value.quiz_questions) {
        const isCurrentQuestionAPoll = currentQuestion.value?.is_poll === true;

        questions.splice(0, questions.length);
        for (const question of value.quiz_questions) {
          questions.push(question);
        }

        if (!isCurrentQuestionAPoll) {
          currentQuestionIdx.value = null;
          goToNextUnansweredQuestion();
        }
      }

      if (value.winner_users) {
        winners.value = value.winner_users;
      }
    },
    { immediate: true, flush: "sync" },
  );

  const hasOpenQuestions = computed(() => {
    return questions.some(
      (question) =>
        question.status !== QuestionStatus.Done && !question.selected_option_id,
    );
  });

  const quiz = computed(() => toValue(quizResult));

  const currentQuestionNumber = computed(
    () => currentQuestion.value?.order ?? null,
  );

  const questionsCount = computed(() => {
    return questions?.length;
  });

  const isPoll = computed(() => {
    return currentQuestion?.value?.is_poll ?? false;
  });

  const hasWinner = computed(() => (winners.value?.length ? true : false));

  function goToPreviousQuestion() {
    if (currentQuestionIdx.value === null) {
      currentQuestionIdx.value = 0;
      return;
    }

    if (currentQuestionIdx.value - 1 < 0) {
      return;
    }
    currentQuestionIdx.value--;
  }

  function goToNextQuestion() {
    if (currentQuestionIdx.value === null) {
      currentQuestionIdx.value = 0;
      return;
    }

    if (currentQuestionIdx.value + 1 >= questions.length) {
      const lastVotedQuestion = questions.find(
        (question) => question.id === lastVotedQuestionId.value,
      );
      if (lastVotedQuestion?.is_poll) lastVotedQuestionId.value = null;

      return;
    }
    currentQuestionIdx.value++;
  }

  function goToNextUnansweredQuestion() {
    let nextQuestionIdx = questions.length - 1;
    const startIdx =
      currentQuestionIdx.value !== null ? currentQuestionIdx.value + 1 : 0;

    for (let i = startIdx; i < questions.length; i++) {
      const question = questions[i]!;

      if (!question.selected_option_id) {
        nextQuestionIdx = i;
        break;
      }
    }

    currentQuestionIdx.value = nextQuestionIdx;
  }

  const finishedData = computed(() => {
    const winner = winners.value?.[0];
    const prize = toValue(quizResult)?.prize;
    const end_time = toValue(quizResult)?.end_time;

    if (!winner || !prize || !end_time) {
      return null;
    }

    const quiz = toValue(quizResult);

    return {
      winner: winner.user,
      created_by_id: quiz?.created_by_id ?? quiz?.created_by?.id ?? null,
      prize: {
        id: prize.id,
        title: (prize.title || prize.description) ?? null,
        image: prize.image ?? null,
        translations: prize.contest_prize_translations
          .map((translation) => translation.title ?? translation.description)
          .filter(isString),
      },
      end_time,
      title: quiz?.description ?? null,
      translations:
        quiz?.quiz_translations?.map((t) => t.description).filter(isString) ??
        [],
      type: ContestType.Quiz,
    } satisfies IContestFinished;
  });

  const definition = reactive({
    currentQuestionNumber,
    currentQuestion,
    questionsCount,
    quiz,
    finishedData,
    isPoll,
    winners,
    hasWinner,
    hasOpenQuestions,
    selectedOptionId,
    quizStatus,
    lastVotedQuestionId,
    goToPreviousQuestion,
    goToNextQuestion,
  });

  provide(quizCardProvideKey, definition);
  return definition;
}

export function useQuizCardState() {
  const value = inject(quizCardProvideKey);

  if (!value) {
    throw new Error(
      "useQuizCardState() is called without any upstream component providing it",
    );
  }

  return value;
}
